import { useState, useEffect } from 'react';
import { Trash2 } from 'lucide-react';
import { useInsertDocument } from '../../hooks/useInsertDocument';
import { useFetchEnabledHours } from '../../hooks/useFetchEnabledHours';
import { useExclusiveHours } from '../../hooks/useExclusiveHours';
import { useDeleteDocument } from '../../hooks/useDeleteDocument';
import { useHours } from '../../hooks/useHours';
import { useFetchDayOff } from '../../hooks/useFetchDayOff';
import {
  getOccupiedAppointmentHours,
  getTerminalBlockedHours,
  hasValidAppointmentHours,
} from '../../utilities/appointmentSlots';
import { formatLocalDateDisplay, formatLocalDateInput } from '../../utilities/dateUtils';
import { useToast } from '../ui';
import { Button } from '../ui';

const uuid = require('uuid');
const PROFESSIONALS = ['Carlos', 'Donizete'];

export default function Lunch() {
  const [lunchDate, setLunchDate] = useState(formatLocalDateInput());
  const [professional, setProfessional] = useState('');
  const [date, setDate] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [hour, setHour] = useState([]);
  const [appointmentHours, setAppointmentHours] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [hours, setHours] = useState([]);

  const { documents } = useFetchDayOff('transactions', lunchDate);
  const { setDocument } = useInsertDocument('transactions');
  const { documents: enabledHours } = useFetchEnabledHours('transactions', professional, date);
  const { hoursObject } = useHours(date);
  const { exclusiveHours } = useExclusiveHours(appointmentHours, hours, date);
  const { deleteData } = useDeleteDocument();
  const toast = useToast();

  useEffect(() => {
    setHours(Array.isArray(hoursObject) ? hoursObject.map((e) => e.hour) : []);
  }, [hoursObject]);

  useEffect(() => {
    if (date && professional && enabledHours) {
      setAppointmentHours(getOccupiedAppointmentHours(enabledHours, getTerminalBlockedHours(hours)));
    } else {
      setAppointmentHours([]);
    }
  }, [date, professional, enabledHours, hours]);

  const handleSetLunch = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!hasValidAppointmentHours(hour) || !hours.includes(hour[0])) {
      toast.error('Selecione um horário válido para o almoço.');
      return;
    }

    setSubmitting(true);
    const tid = uuid.v4();
    const result = await setDocument({
      date, professional,
      professional_id: professional === 'Carlos' ? 1 : 2,
      hour, hours_index: hours.indexOf(hour[0]) + 1,
      service: 'Almoço', name: 'Almoço',
      updatedAt: new Date(), tid, lunch: true,
    }, tid);
    setSubmitting(false);

    if (!result?.success) {
      toast.error(result?.error || 'Não foi possível agendar o almoço. Escolha outro horário.');
    } else {
      toast.success('Almoço agendado com sucesso.');
      setProfessional(''); setDate(''); setHour([]); setEnabled(false);
    }
  };

  const handleCancel = async (tid) => {
    const deleted = await deleteData('transactions', tid);
    if (!deleted) toast.error('Não foi possível cancelar. Tente novamente.');
    else toast.success('Almoço cancelado.');
  };

  return (
    <div className="flex flex-col gap-6">
      {/* Form */}
      <form onSubmit={handleSetLunch} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="lunch-pro" className="text-sm font-medium text-text-secondary">Barbeiro</label>
            <select
              id="lunch-pro"
              required
              value={professional}
              onChange={(e) => { setEnabled(true); setProfessional(e.target.value); }}
              disabled={submitting}
              className="rounded-md border border-border px-3.5 py-2.5 text-base bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus appearance-none"
            >
              <option value="">Selecione</option>
              {PROFESSIONALS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="lunch-date" className="text-sm font-medium text-text-secondary">Data</label>
            <input
              id="lunch-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              min={formatLocalDateInput()}
              disabled={!enabled || submitting}
              required
              className="rounded-md border border-border px-3.5 py-2.5 text-base bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor="lunch-hour" className="text-sm font-medium text-text-secondary">Horário</label>
            <select
              id="lunch-hour"
              value={hour[0] || ''}
              onChange={(e) => setHour(e.target.value ? [e.target.value] : [])}
              disabled={!date || submitting}
              required
              className="rounded-md border border-border px-3.5 py-2.5 text-base bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus appearance-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <option value="">Selecione</option>
              {exclusiveHours.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
          </div>
        </div>

        <div className="flex justify-end">
          <Button type="submit" loading={submitting} disabled={!professional || !date || !hour.length}>
            Agendar almoço
          </Button>
        </div>
      </form>

      {/* Listagem */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="lunch-filter" className="text-sm font-medium text-text-secondary">Filtrar por data</label>
          <input
            id="lunch-filter"
            type="date"
            value={lunchDate}
            onChange={(e) => setLunchDate(e.target.value)}
            className="rounded-md border border-border px-3.5 py-2.5 text-sm bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus w-full sm:w-48"
          />
        </div>

        {documents?.filter((d) => d.lunch).length > 0 ? (
          <ul className="flex flex-col gap-2">
            {documents.filter((d) => d.lunch).map((d) => (
              <li key={d.id} className="flex items-center justify-between gap-3 bg-bg-secondary rounded-lg px-4 py-3">
                <span className="text-sm text-text-primary">
                  <span className="font-semibold">{d.professional}</span>
                  <span className="text-text-muted"> — {formatLocalDateDisplay(d.date)} — {d.hour?.[0]}</span>
                </span>
                <button
                  type="button"
                  onClick={() => handleCancel(d.tid)}
                  aria-label="Cancelar almoço"
                  className="text-text-muted hover:text-danger transition-colors duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus rounded"
                >
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-text-muted py-2">Nenhum almoço encontrado para esta data.</p>
        )}
      </div>
    </div>
  );
}
